import { useId, useRef, useState } from "react";
import styled from "styled-components";

/** CLE-2884 — Ani: "when content is too long collapse it after 15 lines". */
export const DEFAULT_COLLAPSED_LINES = 15;

/**
 * The lines of `text` as the user typed them — what "15 lines" means to Ani and to QA, who count
 * Enter-separated lines, not the visual lines a narrow column wraps them into. Only LF and CRLF
 * break lines (`white-space: pre-wrap` renders a lone CR as a space). Blank lines at the very
 * start and end are dropped for display only; the stored value is untouched.
 */
export const displayLines = (text: string): string[] => {
  const all = text.split(/\r\n|\n/);
  let start = 0;
  let end = all.length;
  while (start < end && all[start].trim() === "") start += 1;
  while (end > start && all[end - 1].trim() === "") end -= 1;
  return all.slice(start, end);
};

/** Whether `text` has more than `maxLines` typed lines once blank edge lines are dropped. */
export const exceedsLineLimit = (text: string, maxLines: number): boolean =>
  displayLines(text).length > maxLines;

const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  color: var(--color-purple);
  font: inherit;
  font-size: 0.875rem;
  cursor: pointer;
`;

export interface ICollapsibleTextProps {
  text: string;
  /** Typed lines shown while collapsed. */
  maxLines?: number;
  className?: string;
}

/**
 * Long free text, cut after `maxLines` TYPED lines with a Show More / Show Less toggle. Each
 * typed line is shown whole — a line that wraps still counts as one — so the cut is the same in
 * every column width and in jsdom; no measuring, no CSS line clamp (which counts visual lines,
 * and showed "14 lines" to QA on 2026-10-14). Line breaks are kept (`white-space: pre-wrap`).
 * Text of `maxLines` lines or fewer is never cut and has no toggle.
 */
export function CollapsibleText({
  text,
  maxLines = DEFAULT_COLLAPSED_LINES,
  className = "text-900",
}: ICollapsibleTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textId = useId();
  const [expanded, setExpanded] = useState(false);

  // New text starts collapsed again — adjusted during render, so it never paints open first.
  const [prevText, setPrevText] = useState(text);
  if (text !== prevText) {
    setPrevText(text);
    setExpanded(false);
  }

  const lines = displayLines(text);
  const overflows = lines.length > maxLines;
  const shown = overflows && !expanded ? lines.slice(0, maxLines) : lines;

  const toggle = () => {
    // Collapsing removes what is below the block's top; if that top has scrolled off-screen,
    // bring it back, or the reader is left looking at whatever slid up into view.
    if (expanded) {
      const container = containerRef.current;
      if (container && container.getBoundingClientRect().top < 0) {
        container.scrollIntoView({ block: "start" });
      }
    }
    setExpanded(!expanded);
  };

  return (
    <div ref={containerRef} style={{ scrollMarginTop: "1rem" }}>
      <div id={textId} className={className} style={{ whiteSpace: "pre-wrap" }}>
        {shown.join("\n")}
      </div>
      {overflows && (
        <ToggleButton
          type="button"
          className="mt-2"
          aria-expanded={expanded}
          aria-controls={textId}
          onClick={toggle}
        >
          {expanded ? (
            <>
              Show Less <i className="pi pi-angle-up ml-1" />
            </>
          ) : (
            <>
              Show More <i className="pi pi-angle-down ml-1" />
            </>
          )}
        </ToggleButton>
      )}
    </div>
  );
}
